/**
 * Источник регистрации, пришедший с лендинга.
 *
 * Файл общий для фронтендов SSO: копия лежит в репозитории expertcrm,
 * src/СlientPortal.IdentityServer.Web/src/services/attributionService.js. Копии должны совпадать,
 * иначе сервисы начнут писать в общую cookie по-разному.
 *
 * Здесь используется только captureFromUrl: кабинет сам регистрацию не отправляет,
 * его задача - не потерять метки при редиректе на identity.
 */
const STORAGE_KEY = 'ec_attribution';
const CONTRACT_VERSION = 1;

const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;

const MAX_TEXT_LENGTH = 1024;
const MAX_URL_LENGTH = 4096;

//всё содержимое cookie ограничено 4 КБ, оставляем запас на атрибуты и соседние cookie
const COOKIE_BUDGET_BYTES = 3500;
const COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

const TEXT_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'yclid'];
const URL_FIELDS = ['landing_url', 'referrer_url', 'handoff_url'];

//порядок отказа при переполнении cookie: handoff наименее ценен, referrer нечем заменить
const URL_FIELDS_DROP_ORDER = ['handoff_url', 'landing_url', 'referrer_url'];

//родительский домен SSO: прод - .expertcentre.org, дев - .featureddata.com, локально пусто (host-only)
const getCookieDomain = () => {
    const configured = (process.env.REACT_APP_ATTRIBUTION_COOKIE_DOMAIN || '').trim();
    if (!configured) {
        return '';
    }

    const host = (window.location.hostname || '').toLowerCase();
    const domain = configured.toLowerCase().replace(/^\./, '');

    //cookie с чужим доменом браузер отбрасывает молча, поэтому на localhost и по IP остаёмся host-only
    return host === domain || host.endsWith(`.${domain}`) ? configured : '';
};

const readCookie = () => {
    let jar;
    try {
        jar = document.cookie || '';
    } catch {
        return null;
    }

    for (const part of jar.split(';')) {
        const separator = part.indexOf('=');
        if (separator === -1) {
            continue;
        }
        if (part.slice(0, separator).trim() !== STORAGE_KEY) {
            continue;
        }
        try {
            return decodeURIComponent(part.slice(separator + 1).trim()) || null;
        } catch {
            return null;
        }
    }

    return null;
};

const buildCookie = (value, maxAgeSeconds) => {
    const attributes = [
        `${STORAGE_KEY}=${encodeURIComponent(value)}`,
        'Path=/',
        `Max-Age=${maxAgeSeconds}`,
        'SameSite=Lax'
    ];

    const domain = getCookieDomain();
    if (domain) {
        attributes.push(`Domain=${domain}`);
    }

    //на локальном http cookie с флагом Secure браузер не сохранит
    if (window.location.protocol === 'https:') {
        attributes.push('Secure');
    }

    return attributes.join('; ');
};

const writeCookie = (value) => {
    try {
        document.cookie = buildCookie(value, COOKIE_MAX_AGE_SECONDS);
    } catch {
        return false;
    }
    //cookie могут быть запрещены, тогда запись молча не состоится
    return readCookie() !== null;
};

const removeCookie = () => {
    try {
        document.cookie = buildCookie('', 0);
    } catch {}
};

//storage may be unavailable (private mode, blocked site data), every access is optional
const getStorages = () => {
    const storages = [];
    try {
        if (window.localStorage) {
            storages.push(window.localStorage);
        }
    } catch {}
    try {
        if (window.sessionStorage) {
            storages.push(window.sessionStorage);
        }
    } catch {}
    return storages;
};

//cookie общего домена - основной носитель: её видят все сервисы SSO. storage остаётся фолбэком
const readRaw = () => {
    const fromCookie = readCookie();
    if (fromCookie) {
        return fromCookie;
    }

    for (const storage of getStorages()) {
        try {
            const value = storage.getItem(STORAGE_KEY);
            if (value) {
                return value;
            }
        } catch {}
    }
    return null;
};

const writeRaw = (value) => {
    if (writeCookie(value)) {
        return true;
    }

    for (const storage of getStorages()) {
        try {
            storage.setItem(STORAGE_KEY, value);
            return true;
        } catch {}
    }
    return false;
};

const removeRaw = () => {
    removeCookie();

    for (const storage of getStorages()) {
        try {
            storage.removeItem(STORAGE_KEY);
        } catch {}
    }
};

/**
 * Укладывает объект в бюджет cookie. Длинные адреса не обрезаются, а опускаются целиком:
 * усечённый URL выглядит достоверным, но ведёт не туда, куда вёл исходный.
 */
const serializeForTransport = (attribution) => {
    let payload = { ...attribution };
    let serialized = JSON.stringify(payload);

    for (const field of URL_FIELDS_DROP_ORDER) {
        if (encodeURIComponent(serialized).length <= COOKIE_BUDGET_BYTES) {
            break;
        }
        if (!payload[field]) {
            continue;
        }
        payload = { ...payload, [field]: '' };
        serialized = JSON.stringify(payload);
    }

    return serialized;
};

const stripControlCharacters = (value) => {
    if (typeof value !== 'string') {
        return '';
    }
    //control characters are stripped here as well as on the server
    // eslint-disable-next-line no-control-regex
    return value.replace(/[\u0000-\u001F\u007F]/g, '').trim();
};

const sanitizeText = (value) => stripControlCharacters(value).slice(0, MAX_TEXT_LENGTH);

const sanitizeUrl = (value) => {
    //адрес не обрезаем по длине: усечённый URL выглядит достоверным, но ведёт не туда, куда вёл исходный
    const text = stripControlCharacters(value);
    if (!text || text.length > MAX_URL_LENGTH) {
        return '';
    }
    try {
        const url = new URL(text);
        return url.protocol === 'http:' || url.protocol === 'https:' ? text : '';
    } catch {
        return '';
    }
};

const isFresh = (capturedAt, now) => {
    if (!Number.isFinite(capturedAt) || capturedAt <= 0) {
        return false;
    }
    if (capturedAt - now > MAX_CLOCK_SKEW_MS) {
        return false;
    }
    return now - capturedAt <= MAX_AGE_MS;
};

const normalize = (source) => {
    if (!source || typeof source !== 'object') {
        return null;
    }

    if (Number(source.version) !== CONTRACT_VERSION) {
        return null;
    }

    const capturedAt = Number(source.capturedAt);
    if (!isFresh(capturedAt, Date.now())) {
        return null;
    }

    const result = { version: CONTRACT_VERSION, capturedAt };

    TEXT_FIELDS.forEach((field) => {
        result[field] = sanitizeText(source[field]);
    });
    URL_FIELDS.forEach((field) => {
        result[field] = sanitizeUrl(source[field]);
    });

    //an empty referrer is legitimate, but a package without any payload is not worth keeping
    const hasPayload = [...TEXT_FIELDS, ...URL_FIELDS].some((field) => result[field] !== '');

    return hasPayload ? result : null;
};

class AttributionService {

    /**
     * Читает метки источника из адресной строки и сохраняет их.
     * Новый валидный пакет заменяет прежний целиком, отсутствие меток ничего не стирает.
     */
    captureFromUrl(search = window.location.search) {
        let query;
        try {
            query = new URLSearchParams(search);
        } catch {
            return null;
        }

        if (!query.get('ec_attribution_v')) {
            return this.get();
        }

        const incoming = {
            version: query.get('ec_attribution_v'),
            capturedAt: query.get('ec_attribution_ts')
        };
        [...TEXT_FIELDS, ...URL_FIELDS].forEach((field) => {
            incoming[field] = query.get(field) || '';
        });

        const attribution = normalize(incoming);
        if (!attribution) {
            return this.get();
        }

        try {
            writeRaw(serializeForTransport(attribution));
        } catch {}

        //возвращаем то, что действительно сохранено: часть адресов могла не влезть в cookie
        return this.get() || attribution;
    }

    /**
     * Возвращает сохраненный источник или null, если его нет либо он больше не действует.
     */
    get() {
        const raw = readRaw();
        if (!raw) {
            return null;
        }

        let parsed;
        try {
            parsed = JSON.parse(raw);
        } catch {
            removeRaw();
            return null;
        }

        const attribution = normalize(parsed);
        if (!attribution) {
            removeRaw();
            return null;
        }

        return attribution;
    }

    /**
     * Формирует поле attribution для запроса регистрации.
     * Канал передается всегда, даже когда рекламных меток нет.
     * Имена полей - camelCase: snake_case остаётся контрактом лендинга, тело запроса к API идёт в camelCase.
     */
    buildSignupPayload() {
        const attribution = this.get();

        if (!attribution) {
            return { channel: 'web', captureMethod: 'none' };
        }

        return {
            channel: 'web',
            captureMethod: 'url',
            version: attribution.version,
            capturedAt: attribution.capturedAt,
            utmSource: attribution.utm_source,
            utmMedium: attribution.utm_medium,
            utmCampaign: attribution.utm_campaign,
            utmContent: attribution.utm_content,
            utmTerm: attribution.utm_term,
            yclid: attribution.yclid,
            landingUrl: attribution.landing_url,
            referrerUrl: attribution.referrer_url,
            handoffUrl: attribution.handoff_url
        };
    }

    clear() {
        removeRaw();
    }
}

const attributionService = new AttributionService();

export default attributionService;
